import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  Card,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Skeleton,
} from '@mui/material'
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Group as GroupIcon,
} from '@mui/icons-material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchGroups,
  fetchLinkedServices,
  selectGroupsByLinkedService,
  selectGroupsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
} from '../manage.slice'
import { useLinkedServiceSelection } from '../hooks/use-linked-service-selection'
import { MIN_BUTTON_SIZE, GROUPS_URLS } from '../constants'
import { deleteGroup } from '../utils/api'
import { GroupWithStats } from './types'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'

export default function GroupsV4(): ReactElement {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()
  let linkedServiceID = useLinkedServiceSelection()

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [searchQuery, setSearchQuery] = useState<string>('')
  let [dialogStatus, setDialogStatus] = useState<string>('')
  let [filterResetMessage, setFilterResetMessage] = useState<string>('')
  let [isDeleting, setIsDeleting] = useState<boolean>(false)

  let linkedServices = useAppSelector(selectLinkedServices)
  let fetchLinkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let fetchGroupsStatus = useAppSelector(selectGroupsStatus)
  let groups = useAppSelector(
    selectGroupsByLinkedService(linkedServiceID || ''),
  )

  useAutoFetch(fetchGroupsStatus, fetchGroups)
  useAutoFetch(fetchLinkedServicesStatus, fetchLinkedServices)

  let resetFilters = useCallback(() => {
    setSearchQuery('')
    setFilterResetMessage('Filters have been reset')
    setTimeout(() => setFilterResetMessage(''), 3000)
  }, [])

  let groupsWithStats: GroupWithStats[] = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        userCount: g.users?.length || 0,
      })),
    [groups],
  )

  let filteredGroups = useMemo(() => {
    if (!linkedServiceID) return []

    let query = searchQuery.toLowerCase()
    return groupsWithStats.filter((g) => {
      // Check search query
      if (searchQuery && !g.name.toLowerCase().includes(query)) return false
      return true
    })
  }, [groupsWithStats, linkedServiceID, searchQuery])

  let stats = useMemo(() => {
    let totalForService = groupsWithStats.length

    return {
      totalCount: totalForService,
      filteredCount: filteredGroups.length,
      isFiltered: searchQuery !== '',
      hasGroups: totalForService > 0,
      totalUsers: filteredGroups.reduce((sum, g) => sum + g.userCount, 0),
    }
  }, [filteredGroups, groupsWithStats, searchQuery])

  let isLoading =
    fetchGroupsStatus === 'pending' || fetchLinkedServicesStatus === 'pending'

  let handleViewGroup = useCallback(
    (groupId: string) => {
      navigate(`${GROUPS_URLS.INDEX}/${groupId}`)
    },
    [navigate],
  )

  let handleCreateNew = useCallback(() => {
    navigate(GROUPS_URLS.CREATE, {
      state: { linkedServiceID },
    })
  }, [navigate, linkedServiceID])

  let handleDeleteGroup = useCallback(
    async (group: GroupWithStats) => {
      if (!group.id) return

      setIsDeleting(true)

      await deleteGroup(group.id!)
        .then(() => dispatch(fetchGroups()))
        .then(() => showSuccess(`Group "${group.name}" deleted successfully.`))
        .catch((err) => {
          let errorMessage = `Error deleting group "${group.name}".`
          // eslint-disable-next-line no-console
          console.error(errorMessage, 'Group ID: ', group.id, 'Error', err)
          showError(errorMessage)
        })
        .finally(() => {
          setIsDeleting(false)
        })
    },
    [dispatch, showSuccess, showError],
  )

  let groupCountText =
    stats.totalCount === 1
      ? `${stats.totalCount} group`
      : `${stats.totalCount} groups`

  // Reset filters when linked service changes
  useEffect(() => {
    resetFilters()
  }, [linkedServiceID, resetFilters])

  // Handle success message from navigation state
  useEffect(() => {
    let state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      showSuccess(state.successMessage)
      // Clear the state to prevent message showing on refresh
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate, showSuccess])

  return (
    <Container maxWidth="xl" className="py-6">
      <Typography variant="h4" component="h1" className="mb-6">
        Groups
      </Typography>
      <Card className="grid grid-cols-1 gap-6 p-6 mb-6 lg:grid-cols-2 lg:items-center">
        <FormControl fullWidth>
          <InputLabel id="linked-service-label">Linked Service</InputLabel>
          <Select
            labelId="linked-service-label"
            value={linkedServiceID || ''}
            onChange={(e) =>
              dispatch(activeLinkedServiceChanged(e.target.value))
            }
            label="Linked Service"
            aria-label="Select linked service"
            sx={{ backgroundColor: 'background.paper' }}
          >
            <MenuItem value="">
              <em>Select a linked service</em>
            </MenuItem>
            {linkedServices.map((service) => (
              <MenuItem key={service.id} value={service.id}>
                {service.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Search Groups"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon className="mr-2" />,
            },
          }}
          disabled={!linkedServiceID || !stats.hasGroups}
          aria-label="Search groups by name"
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'background.paper' },
          }}
        />
      </Card>
      <Card className="p-6">
        <Box className="flex flex-wrap items-center justify-between mb-6">
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            disabled={!linkedServiceID}
            onClick={handleCreateNew}
            aria-label="Create new group"
            className="mb-4 md:mb-0 basis-full md:basis-auto"
          >
            Create Group
          </Button>
          <Box
            className="flex items-center justify-end gap-2 grow"
            role="status"
            aria-live="polite"
            aria-label="Group statistics"
          >
            <Chip
              size="medium"
              label={groupCountText}
              color="primary"
              aria-label={`Total: ${groupCountText}`}
            />
            {stats.isFiltered && (
              <>
                <Chip
                  size="medium"
                  label={`${stats.filteredCount} of ${stats.totalCount} shown`}
                  color="secondary"
                  variant="outlined"
                  aria-label={`Showing ${stats.filteredCount} of ${stats.totalCount} groups after filtering`}
                />
                <Button
                  size="medium"
                  variant="outlined"
                  color="secondary"
                  onClick={resetFilters}
                  aria-label="Reset search and category filters"
                  className="ml-2 whitespace-nowrap"
                >
                  Reset Filters
                </Button>
              </>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Skeleton
            variant="rectangular"
            height={120}
            role="status"
            aria-live="polite"
            aria-label="Loading groups"
          />
        ) : !linkedServiceID ? (
          <Box className="py-12 text-center" role="status">
            <Typography variant="body1" color="text.secondary">
              Please select a linked service to view groups
            </Typography>
          </Box>
        ) : filteredGroups.length === 0 ? (
          <Box className="py-12 text-center" role="status">
            <Typography variant="body1" color="text.secondary">
              No groups found
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table aria-label="Groups table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGroups.map((group) => (
                  <TableRow
                    key={group.id}
                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell className="whitespace-nowrap">
                      <Box className="flex items-center gap-2">
                        <GroupIcon color="action" />
                        {group.name}
                      </Box>
                    </TableCell>
                    <TableCell>{group.userCount}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {group.createdTime
                        ? new Date(group.createdTime).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="right" className="whitespace-nowrap">
                      <Tooltip title="View details">
                        <IconButton
                          onClick={() => handleViewGroup(group.id!)}
                          aria-label={`View ${group.name}`}
                          sx={{
                            minWidth: MIN_BUTTON_SIZE,
                            minHeight: MIN_BUTTON_SIZE,
                          }}
                        >
                          <ViewIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() =>
                            navigate(`${GROUPS_URLS.INDEX}/${group.id}/edit`, {
                              state: { edit: true },
                            })
                          }
                          aria-label={`Edit ${group.name}`}
                          sx={{
                            minWidth: MIN_BUTTON_SIZE,
                            minHeight: MIN_BUTTON_SIZE,
                          }}
                        >
                          <EditIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => setDialogStatus(`delete-${group.id}`)}
                          aria-label={`Delete ${group.name}`}
                          color="error"
                          sx={{
                            minWidth: MIN_BUTTON_SIZE,
                            minHeight: MIN_BUTTON_SIZE,
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <DeleteConfirmationModal
                        isOpen={dialogStatus === `delete-${group.id}`}
                        onClose={() => setDialogStatus('')}
                        onConfirm={() => handleDeleteGroup(group)}
                        entityName="group"
                        entityDisplayName={group.name}
                        isSubmitting={isDeleting}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      {/* Screen reader announcements */}
      {filterResetMessage && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          {filterResetMessage}
        </Box>
      )}
      {NotificationComponent}
    </Container>
  )
}
